/**
 * Places the native WebCodecs sender's clock on the host wall clock.
 *
 * PlayJector stamps every fragment with `sentUs` from a steady clock and, once per health
 * tick, reports a simultaneous reading of that clock and its wall clock in the
 * `webcodecs-telemetry` state message. With that anchor a guest's `webcodecs-sent` latency
 * report (keyed by `sentUs`) can be answered with the host-side send wall time, the same input
 * `CalibratedLatencyTracker` takes from the insertable-streams tap on the Electron path.
 */
export class SenderClockAnchor {
  private clockNowUs: number | null = null;
  private wallNowMs: number | null = null;

  /** Longest distance a `sentUs` may sit from the latest anchor before it is treated as a different clock. */
  constructor(private readonly maxDistanceMs = 60_000) {}

  /** Reads the anchor fields off a telemetry body; anything but two finite positive numbers is ignored. */
  observe(body: { clockNowUs?: unknown; wallNowMs?: unknown } | undefined): boolean {
    const clockNowUs = body?.clockNowUs, wallNowMs = body?.wallNowMs;
    if (typeof clockNowUs !== 'number' || typeof wallNowMs !== 'number') return false;
    if (!Number.isFinite(clockNowUs) || !Number.isFinite(wallNowMs) || clockNowUs <= 0 || wallNowMs <= 0) return false;
    this.clockNowUs = clockNowUs; this.wallNowMs = wallNowMs;
    return true;
  }

  known(): boolean { return this.clockNowUs !== null; }

  /** Wall-clock milliseconds at which the sender read `sentUs`, or undefined without a usable anchor. */
  wallMsFor(sentUs: number): number | undefined {
    if (this.clockNowUs === null || this.wallNowMs === null || !Number.isFinite(sentUs)) return undefined;
    const offsetMs = (sentUs - this.clockNowUs) / 1000;
    if (Math.abs(offsetMs) > this.maxDistanceMs) return undefined;
    return this.wallNowMs + offsetMs;
  }

  clear(): void { this.clockNowUs = null; this.wallNowMs = null; }
}
